import { ReactNode } from "react";
import { ShoppingCart, DollarSign, Receipt } from "lucide-react";
import { PedidoCard } from "@/services/suprimentos";
import { formatCurrency, formatNumber } from "@/utils/format";
import { PedidosTable } from "./PedidosTable";

/* ─── Cartão de estatística ─────────────────────────────────────────────── */
interface StatCardProps {
  label:      string;
  value:      string;
  icon:       ReactNode;
  iconBg:     string;
  valueColor: string;
  subtitle?:  string;
}

function StatCard({ label, value, icon, iconBg, valueColor, subtitle }: StatCardProps) {
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 px-4 py-3 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500 font-medium leading-none mb-1">{label}</p>
        <p className={`text-base font-bold leading-none ${valueColor}`}>{value}</p>
        {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ─── Título do modal (ícone + nome da entidade) ────────────────────────── */
export function ModalEntityTitle({
  icon,
  label,
  iconBg = "bg-brand-50",
  iconColor = "text-brand-600",
}: {
  icon: ReactNode;
  label: string;
  iconBg?: string;
  iconColor?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <span className={iconColor}>{icon}</span>
      </div>
      <span className="text-base font-bold text-slate-800 uppercase tracking-wide leading-tight">
        {label}
      </span>
    </div>
  );
}

/* ─── Conteúdo padrão do modal de pedidos ───────────────────────────────── */
export interface PedidosModalContentProps {
  pedidos:      PedidoCard[];
  hideCols?:    ("centro" | "fornecedor")[];
  expandable?:  boolean;
  /** Stats customizados — se omitido, são calculados automaticamente */
  customStats?: StatCardProps[];
  /** Banner colorido opcional (ex.: para status de pedidos) */
  banner?: { label: string; value: string; color: string };
}

export function PedidosModalContent({
  pedidos,
  hideCols   = [],
  expandable = false,
  customStats,
  banner,
}: PedidosModalContentProps) {
  const total       = pedidos.reduce((s, p) => s + (p.value ?? 0), 0);
  const ticketMedio = pedidos.length > 0 ? total / pedidos.length : 0;

  const stats: StatCardProps[] = customStats ?? [
    {
      label:      "Qtd. Pedidos",
      value:      formatNumber(pedidos.length),
      subtitle:   "Pedidos realizados",
      icon:       <ShoppingCart className="w-4.5 h-4.5" />,
      iconBg:     "bg-blue-50",
      valueColor: "text-slate-800",
    },
    {
      label:      "Valor Total",
      value:      formatCurrency(total),
      subtitle:   "Valor faturado",
      icon:       <DollarSign className="w-4.5 h-4.5" />,
      iconBg:     "bg-emerald-50",
      valueColor: "text-emerald-600",
    },
    {
      label:      "Ticket Médio",
      value:      formatCurrency(ticketMedio),
      subtitle:   "Média por pedido",
      icon:       <Receipt className="w-4.5 h-4.5" />,
      iconBg:     "bg-purple-50",
      valueColor: "text-purple-600",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Banner colorido opcional */}
      {banner && (
        <div
          className="flex items-center justify-between px-4 py-2.5 rounded-xl text-white text-sm font-semibold"
          style={{ background: banner.color }}
        >
          <span>{banner.label}</span>
          <span>{banner.value}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* Tabela paginada */}
      <PedidosTable
        pedidos={pedidos}
        hideCols={hideCols}
        expandable={expandable}
      />
    </div>
  );
}
